import { supabase } from '@/lib/supabase'
import { TourInstance } from '@/types'
import { useQuery } from '@tanstack/react-query'
import { addDays, format } from 'date-fns'
import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin, Users } from 'lucide-react'
import React from 'react'
import { Link } from 'react-router-dom'

interface TourSchedulePreviewProps {
  prefersReducedMotion?: boolean
}

const TourSchedulePreview: React.FC<TourSchedulePreviewProps> = ({
  prefersReducedMotion = false
}) => {
  // Fetch upcoming scheduled tours for next 14 days
  const { data: scheduledTours, isLoading } = useQuery({
    queryKey: ['homepage-tour-schedule'],
    queryFn: async () => {
      const today = new Date()
      const fourteenDaysLater = addDays(today, 14)

      const { data, error } = await supabase
        .from('tour_instances')
        .select(`
          *,
          tour_schedule:tour_schedules(
            *,
            tour:tours(title, slug, level, duration_days)
          )
        `)
        .gte('date', format(today, 'yyyy-MM-dd'))
        .lte('date', format(fourteenDaysLater, 'yyyy-MM-dd'))
        .eq('status', 'scheduled')
        .order('date', { ascending: true })
        .limit(20)

      if (error) throw error

      // Filter out fully booked tours and group multi-day tours
      const availableTours = data.filter(instance => {
        const currentBookings = instance.current_bookings || 0
        const maxCapacity = instance.max_capacity || 0
        return currentBookings < maxCapacity
      })

      const groupedTours = availableTours.reduce((groups: any[], instance: TourInstance) => {
        const isMultiDay = instance.notes?.includes(' of ')

        if (isMultiDay && instance.notes) {
          const match = instance.notes.match(/Day (\d+) of (\d+)/)
          if (match) {
            const [, dayNum, totalDays] = match
            const dayNumber = parseInt(dayNum)

            // Only include Day 1 instances for multi-day tours
            if (dayNumber === 1) {
              const tourData = {
                ...instance,
                isMultiDay: true,
                totalDays: parseInt(totalDays),
                displayTitle: `${instance.tour_schedule?.tour?.title}`,
                availableSpots: instance.max_capacity - instance.current_bookings
              }
              groups.push(tourData)
            }
          }
        } else {
          // Single day tour - check if it's a half-day or full-day
          const durationDays = instance.tour_schedule?.tour?.duration_days || 1
          const isHalfDay = durationDays < 1 || durationDays === 0.5

          const tourData = {
            ...instance,
            isMultiDay: false,
            totalDays: isHalfDay ? 0.5 : 1,
            displayTitle: instance.tour_schedule?.tour?.title,
            availableSpots: instance.max_capacity - instance.current_bookings
          }
          groups.push(tourData)
        }

        return groups
      }, [])

      return groupedTours.slice(0, 6) // Show max 6 upcoming tours
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  })

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'bg-garage-green text-garage-dark'
      case 'intermediate':
        return 'bg-garage-accent text-garage-cream'
      case 'expert':
        return 'bg-garage-red text-garage-cream'
      default:
        return 'bg-garage-metal text-garage-cream'
    }
  }

  const getUrgencyStyle = (availableSpots: number, maxCapacity: number) => {
    const occupancyRate = ((maxCapacity - availableSpots) / maxCapacity) * 100

    if (occupancyRate >= 80) {
      return {
        urgency: 'Almost Full!',
        style: 'text-garage-red bg-garage-red/10 border-garage-red/30'
      }
    } else if (occupancyRate >= 60) {
      return {
        urgency: 'Filling Fast',
        style: 'text-garage-accent bg-garage-accent/10 border-garage-accent/30'
      }
    } else {
      return {
        urgency: 'Available',
        style: 'text-garage-green bg-garage-green/10 border-garage-green/30'
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-garage-accent"></div>
      </div>
    )
  }

  if (!scheduledTours || scheduledTours.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 mx-auto mb-4 text-garage-text/50" />
        <p className="text-garage-text/70">No available tours in the next 14 days.</p>
        <p className="text-sm text-garage-text/50 mt-2">All tours may be fully booked. Contact us for custom dates or check back soon!</p>
      </div>
    )
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {scheduledTours.map((tour: any, index: number) => {
        const startDate = new Date(tour.date)
        const endDate = tour.isMultiDay
          ? addDays(startDate, tour.totalDays - 1)
          : startDate

        const dateDisplay = tour.isMultiDay
          ? `${format(startDate, 'MMM dd')} - ${format(endDate, 'dd')}`
          : format(startDate, 'MMM dd')

        const urgencyInfo = getUrgencyStyle(tour.availableSpots, tour.max_capacity)

        return (
          <motion.div
            key={`${tour.id}-${tour.date}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              delay: prefersReducedMotion ? 0 : index * 0.1,
              duration: prefersReducedMotion ? 0.4 : 0.6
            }}
            className="bg-garage-card rounded-lg border border-garage-border hover:border-garage-accent transition-all duration-300 overflow-hidden group hover:shadow-garage-hover"
          >
            {/* Header with Date and Urgency */}
            <div className="bg-garage-surface p-4 border-b border-garage-border">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="text-2xl font-bold text-garage-dark font-garage-title">
                    {dateDisplay}
                  </div>
                  <div className="text-sm text-garage-text/70 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Starting at {tour.start_time || '09:00'}
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium border ${urgencyInfo.style}`}>
                  {urgencyInfo.urgency}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(tour.tour_schedule?.tour?.level || 'intermediate')}`}>
                  {tour.tour_schedule?.tour?.level || 'intermediate'}
                </span>
              </div>
            </div>

            {/* Tour Details */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-garage-dark mb-2 font-garage-title line-clamp-1">
                {tour.displayTitle}
              </h3>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-garage-text">
                  <Users className="h-4 w-4 text-garage-accent" />
                  <span>{tour.availableSpots} of {tour.max_capacity} spots available</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-garage-text">
                  <MapPin className="h-4 w-4 text-garage-accent" />
                  <span>Vang Vieng • Starting ${tour.price}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-garage-text/70 mb-1">
                  <span>Booked</span>
                  <span>{tour.current_bookings}/{tour.max_capacity}</span>
                </div>
                <div className="w-full bg-garage-border rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-garage-accent to-garage-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(tour.current_bookings / tour.max_capacity) * 100}%` }}
                  />
                </div>
              </div>

              {/* Action Button */}
              <Link
                to={`/contact?tour=${tour.id}&date=${tour.date}`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-garage-accent hover:bg-garage-primary text-garage-cream font-medium text-sm rounded-lg transition-all duration-300 group-hover:shadow-lg"
              >
                <Calendar className="h-4 w-4" />
                Book This Date
              </Link>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}

export default TourSchedulePreview
